// Virtual Marketer Primeo - Utils
// Version: 1.0.0

import { ChatMessageItem, SerializableChatMessage } from './types';
import { LOCAL_STORAGE_KEY, MAX_MESSAGES_TO_STORE } from './constants';

/**
 * Save messages to local storage
 */
export const saveMessagesToLocalStorage = (messages: ChatMessageItem[]): void => {
    try {
        const serializableMessages = messages
            .filter(msg => typeof msg.content === 'string' && !msg.isStreaming)
            .slice(-MAX_MESSAGES_TO_STORE)
            .map((msg): SerializableChatMessage => ({
                id: msg.id,
                content: msg.content as string,
                sender: msg.sender,
                timestamp: msg.timestamp.toISOString(),
                unifiedSearchResults: msg.unifiedSearchResults,
                searchResultLayout: msg.searchResultLayout,
                areProductsVisible: msg.areProductsVisible,
            }));
        
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(serializableMessages));
    } catch (error) {
        console.error("Could not save messages to local storage:", error);
    }
};

/**
 * Load messages from local storage
 */
export const loadMessagesFromLocalStorage = (): ChatMessageItem[] => {
    try {
        const storedMessages = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (!storedMessages) {
            return [];
        }
        
        const parsedMessages: SerializableChatMessage[] = JSON.parse(storedMessages);
        
        if (!Array.isArray(parsedMessages)) {
            return [];
        }

        return parsedMessages.map((msg): ChatMessageItem => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
            isStreaming: false,
        }));
    } catch (error) {
        console.error("Could not load messages from local storage:", error);
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        return [];
    }
};

/**
 * Count words in a string
 */
export const countWords = (text: string): number => {
    if (!text || typeof text !== 'string') {
        return 0;
    }
    const words = text.trim().split(/\s+/);
    return words[0] === '' ? 0 : words.length;
};

/**
 * Add UTM parameters to URL
 */
export const addUtmParametersToUrl = (url: string): string => {
    if (!url) return '#';

    const utmSource = 'VirtualMarketerPrimeo';
    const utmMedium = 'VMPAI';
    let utmParams = `utm_source=${encodeURIComponent(utmSource)}&utm_medium=${encodeURIComponent(utmMedium)}`;

    const baseUrl = url.split('#')[0];
    if (baseUrl.includes('utm_source=VirtualMarketerPrimeo') && baseUrl.includes('utm_medium=VMPAI')) {
        return url;
    }
    
    const anchorPart = url.includes('#') ? '#' + url.split('#')[1] : '';
    const urlWithoutAnchor = baseUrl;

    if (urlWithoutAnchor.includes('?')) {
        const lastChar = urlWithoutAnchor.charAt(urlWithoutAnchor.length - 1);
        if (lastChar === '?' || lastChar === '&') {
            return `${urlWithoutAnchor}${utmParams}${anchorPart}`;
        }
        return `${urlWithoutAnchor}&${utmParams}${anchorPart}`;
    } else {
        return `${urlWithoutAnchor}?${utmParams}${anchorPart}`;
    }
};

/**
 * Generate session ID
 */
export const generateSessionId = (): string => {
    return `session-${Date.now()}-${Math.random().toString(36).substring(2)}`;
};
